/**
 * The browser's install prompt, captured once at module load.
 *
 * Chromium fires `beforeinstallprompt` as soon as the page passes its
 * installability checks, which is often before React has mounted; a
 * listener added in an effect would miss it and the app would never be
 * able to install. So this module is imported from the entry point and
 * listens from the first script tick, keeping the event for whoever asks.
 *
 * Safari (iOS and macOS) never fires it: there, installing is the user's
 * own gesture through the Share sheet, and the UI shows the steps.
 */
export interface BeforeInstallPromptEvent extends Event {
	prompt: () => Promise<void>;
	userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

type Listener = () => void;

let deferred: BeforeInstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<Listener>();
const notify = () => {
	for (const l of listeners) l();
};

if (typeof window !== "undefined") {
	window.addEventListener("beforeinstallprompt", (event) => {
		// Keep the browser's own mini-infobar out of the way; the app's sheet asks.
		event.preventDefault();
		deferred = event as BeforeInstallPromptEvent;
		notify();
	});
	window.addEventListener("appinstalled", () => {
		deferred = null;
		installed = true;
		notify();
	});
}

export const isInstallPromptAvailable = (): boolean => deferred !== null;
export const wasInstalledThisVisit = (): boolean => installed;

export const subscribeInstallPrompt = (listener: Listener): (() => void) => {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
};

export type InstallOutcome = "accepted" | "dismissed" | "unavailable";

/** Shows the browser's install dialog if one was captured; the event is spent either way. */
export async function showInstallPrompt(): Promise<InstallOutcome> {
	const event = deferred;
	if (!event) return "unavailable";
	deferred = null;
	notify();
	await event.prompt();
	const { outcome } = await event.userChoice;
	return outcome;
}

export const isRunningStandalone = (): boolean => {
	if (typeof window === "undefined") return false;
	const nav = navigator as Navigator & { standalone?: boolean };
	return window.matchMedia?.("(display-mode: standalone)").matches || nav.standalone === true;
};
